import { startTransition } from 'react';
import { addRecentWorkspaceItem } from '../../utils/recentWorkspaceItems';
import { takeCampaignMissionFocus } from '../../utils/navigationBridge';
import { runAfterPaint, finishTabLoad } from './utils';

/**
 * Restore this tab's campaign/template view. Always writes a value so a previous
 * tab's preferred view cannot leak onto the next one.
 *
 * skipUrlUpdate: restoring must not rewrite the tab (that race applied the next
 * tab's view onto the still-mounted previous campaign, then saved that campaign's
 * view id onto the new tab).
 */
export function applyTabPreferredView(tab, setPreferredDetailView) {
  if (!setPreferredDetailView || !tab) return;
  const type = tab.viewMode || 'flow';
  const preferred = { type, skipUrlUpdate: true };
  if (tab.customViewId) {
    preferred.customViewId = tab.customViewId;
  }
  setPreferredDetailView(preferred);
}

/**
 * Optimistic campaign tab switch: show loader, then select + load after paint.
 */
export function startCampaignTabNavigation({
  sprint,
  isNew,
  seq,
  tabNavigationSeqRef,
  clearActiveView,
  setIsLoadingItem,
  setSelectedSprintId,
  setSelectedTemplateId,
  setCurrentView,
  setLastSelectedCampaignId,
  loadFullSprint,
  VIEWS,
  currentView,
  tab,
  setPreferredDetailView,
  setPendingMissionFocus,
}) {
  if (isNew) {
    setIsLoadingItem?.(true);
  }

  clearActiveView?.();

  addRecentWorkspaceItem({
    type: 'campaign',
    id: sprint.id,
    label: sprint.name,
    icon: sprint.icon,
  });

  const applyWorkspace = () => {
    // Same commit as the id change so SprintDetail never applies B's view onto A.
    applyTabPreferredView(tab, setPreferredDetailView);
    setSelectedSprintId(sprint.id);
    setSelectedTemplateId(null);
    setCurrentView(VIEWS.SPRINT_DETAIL);
  };

  const loadAndFinish = async () => {
    if (seq !== tabNavigationSeqRef.current) return;
    setLastSelectedCampaignId(sprint.id);

    const loadingPromise = !Array.isArray(sprint.tasks)
      ? loadFullSprint(sprint.id)
      : Promise.resolve();

    await finishTabLoad({
      seq,
      tabNavigationSeqRef,
      isNew,
      loadingPromise,
      setIsLoadingItem,
    });

    if (seq !== tabNavigationSeqRef.current) return;
    // Center the mission only after this campaign's tasks are in. Setting it
    // earlier was treated as already handled and the new tab stayed on the campaign.
    const missionId = takeCampaignMissionFocus(sprint.id);
    if (missionId) setPendingMissionFocus?.(missionId);
  };

  // Leaving Docs/Dashboards: flip the area now so the doc sheet doesn't linger.
  // Campaign-to-campaign still waits a paint so the loader can show first.
  const leavingOtherArea =
    currentView === VIEWS.DOCS || currentView === VIEWS.DASHBOARDS;

  if (leavingOtherArea) {
    applyWorkspace();
    loadAndFinish();
    return;
  }

  runAfterPaint(async () => {
    if (seq !== tabNavigationSeqRef.current) return;

    startTransition(() => {
      applyWorkspace();
    });
    await loadAndFinish();
  });
}

/**
 * Optimistic template tab switch: show loader, then select + load after paint.
 */
export function startTemplateTabNavigation({
  template,
  isNew,
  seq,
  tabNavigationSeqRef,
  clearActiveView,
  setIsLoadingItem,
  setSelectedSprintId,
  setSelectedTemplateId,
  setCurrentView,
  loadFullTemplate,
  VIEWS,
  currentView,
  tab,
  setPreferredDetailView,
}) {
  if (isNew) {
    setIsLoadingItem?.(true);
  }

  clearActiveView?.();

  addRecentWorkspaceItem({
    type: 'template',
    id: template.id,
    label: template.name,
    icon: template.icon,
  });

  const applyWorkspace = () => {
    applyTabPreferredView(tab, setPreferredDetailView);
    setSelectedTemplateId(template.id);
    setSelectedSprintId(null);
    setCurrentView(VIEWS.TEMPLATE_DETAIL);
  };

  const loadAndFinish = async () => {
    if (seq !== tabNavigationSeqRef.current) return;

    const loadingPromise = !template.tasks
      ? loadFullTemplate(template.id)
      : Promise.resolve();

    await finishTabLoad({
      seq,
      tabNavigationSeqRef,
      isNew,
      loadingPromise,
      setIsLoadingItem,
    });
  };

  const leavingOtherArea =
    currentView === VIEWS.DOCS || currentView === VIEWS.DASHBOARDS;

  if (leavingOtherArea) {
    applyWorkspace();
    loadAndFinish();
    return;
  }

  runAfterPaint(async () => {
    if (seq !== tabNavigationSeqRef.current) return;

    startTransition(() => {
      applyWorkspace();
    });
    await loadAndFinish();
  });
}

/**
 * View tab switch. Returns whether this navigation is still current.
 */
export async function navigateViewTab({
  tab,
  seq,
  tabNavigationSeqRef,
  activeViewId,
  handleSelectView,
  setIsLoadingItem,
  setCurrentView,
  VIEWS,
  currentView,
}) {
  // Show loading immediately (optimistic) — otherwise nothing changes on
  // screen until the view fetch resolves, which feels like a dead click
  const isNewView = activeViewId !== tab.targetId;
  if (isNewView) {
    setIsLoadingItem?.(true);
  }

  if (currentView === VIEWS.DOCS || currentView === VIEWS.DASHBOARDS) {
    setCurrentView(VIEWS.SPRINT_DETAIL);
  }

  addRecentWorkspaceItem({
    type: 'view',
    id: tab.targetId,
    label: tab.label,
    icon: tab.icon,
  });

  try {
    await handleSelectView({ id: tab.targetId });
  } finally {
    // Only clear if no newer navigation took over (that one owns the flag)
    if (isNewView && seq === tabNavigationSeqRef.current) {
      setIsLoadingItem?.(false);
    }
  }

  return seq === tabNavigationSeqRef.current;
}
